/**
 * observationVisibilityHelpers.js
 *
 * Centralized visibility and actionability helpers for Safety Observations.
 * Decouples workflow stage (status) from user actionability and viewer visibility.
 */

export const MAKER_PENDING_STATUSES = [
  "pending_maker",
  "pending_contractor_maker",
];

export const CHECKER_REVIEW_STATUSES = [
  "pending_checker",
  "pending_contractor_checker",
];

export const FINAL_STATUSES = ["approved", "rejected", "closed_rejected", "completed"];

export const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

/**
 * Checks if the observation is currently actionable by a Maker.
 */
export function isObservationMakerActionable(item, userId) {
  if (!item) return false;
  const status = normalizeStatus(item.status);
  if (!MAKER_PENDING_STATUSES.includes(status)) return false;

  // 1. Trust viewer metadata from backend if available
  if (item.viewer && typeof item.viewer.can_rectify === "boolean") {
    return item.viewer.can_rectify;
  }

  // 2. Fallback: if maker_id is assigned, only that maker can act
  if (item.maker_id != null) {
    return Number(item.maker_id) === Number(userId);
  }

  // 3. If unclaimed in pending_maker/pending_contractor_maker, any eligible maker can claim/rectify
  return true;
}

/**
 * Checks if the observation is currently actionable by a Checker.
 */
export function isObservationCheckerActionable(item, userId, userType) {
  if (!item) return false;
  const status = normalizeStatus(item.status);
  if (!CHECKER_REVIEW_STATUSES.includes(status)) return false;

  // 1. Trust viewer metadata from backend if available
  if (item.viewer && typeof item.viewer.can_review === "boolean") {
    return item.viewer.can_review;
  }

  // 2. Fallback based on userType and status
  const normalizedType = String(userType || "").toUpperCase();
  if (normalizedType === "EXTERNAL") {
    if (status !== "pending_contractor_checker") return false;
    if (item.checker_id != null) {
      return Number(item.checker_id) === Number(userId);
    }
    return true;
  }

  if (normalizedType === "INTERNAL") {
    if (status !== "pending_checker") return false;
    if (item.checker_id != null) {
      return Number(item.checker_id) === Number(userId);
    }
    return true;
  }

  return false;
}

/**
 * Checks if the observation should be visible to a Maker in their dashboard.
 * A Maker can see it if they can act on it, if they participated in any attempt,
 * or if they own it.
 */
export function isObservationVisibleToMaker(item, userId) {
  if (!item) return false;

  if (item.viewer) {
    return Boolean(
      item.viewer.can_rectify ||
        item.viewer.has_participated ||
        item.viewer.is_maker_owner ||
        item.viewer.is_assigned_maker ||
        ["MAKER", "CONTRACTOR_MAKER"].includes(
          String(item.viewer.current_assignment_role || "").toUpperCase(),
        ),
    );
  }

  // Fallback:
  if (Number(item.maker_id) === Number(userId)) return true;
  if (Array.isArray(item.assignments)) {
    const hasMakerAsgn = item.assignments.some(
      (a) =>
        Number(a.safety_officer_id) === Number(userId) &&
        ["MAKER", "CONTRACTOR_MAKER"].includes(
          String(a.assignment_role || "").toUpperCase(),
        ),
    );
    if (hasMakerAsgn) return true;
  }
  const status = normalizeStatus(item.status);
  if (MAKER_PENDING_STATUSES.includes(status)) return true;

  return false;
}

/**
 * Checks if the observation should be visible to a Checker in their dashboard.
 * A Checker can see it if they created it, own it, can review it, are assigned to it, or participated historically.
 */
export function isObservationVisibleToChecker(item, userId) {
  if (!item) return false;

  if (item.viewer) {
    return Boolean(
      item.viewer.can_review ||
        item.viewer.has_participated ||
        item.viewer.is_checker_owner ||
        item.viewer.is_creator ||
        item.viewer.is_assigned_checker ||
        ["CHECKER", "CONTRACTOR_CHECKER"].includes(
          String(item.viewer.current_assignment_role || "").toUpperCase(),
        ),
    );
  }

  // Fallback:
  if (Number(item.creator_id) === Number(userId)) return true;
  if (Number(item.checker_id) === Number(userId)) return true;
  if (Array.isArray(item.assignments)) {
    const hasCheckerAsgn = item.assignments.some(
      (a) =>
        Number(a.safety_officer_id) === Number(userId) &&
        ["CHECKER", "CONTRACTOR_CHECKER"].includes(
          String(a.assignment_role || "").toUpperCase(),
        ),
    );
    if (hasCheckerAsgn) return true;
  }
  const status = normalizeStatus(item.status);
  if (CHECKER_REVIEW_STATUSES.includes(status)) return true;

  return false;
}

/**
 * Helper to determine action button type and badge details for a given row.
 */
export function getObservationRowAction({
  item,
  viewerRole, // "MAKER" | "CHECKER"
  userId,
  userType, // "INTERNAL" | "EXTERNAL"
  section, // "pending_rectification" | "pending_review" | "completed"
}) {
  const isMaker = viewerRole === "MAKER";
  const status = normalizeStatus(item?.status);
  const attemptNo = Number(
    item?.current_attempt_no ?? item?.attempt_no ?? 1
  );

  if (isMaker) {
    if (section === "pending_rectification") {
      const actionable = isObservationMakerActionable(item, userId);
      return {
        type: actionable ? "action" : "view",
        label: actionable ? "Rectify now" : "View Details",
        badgeLabel: actionable ? "Rectification Pending" : "Rectifying",
        badgeTone: actionable ? "red" : "orange",
      };
    }
    if (section === "pending_review") {
      return {
        type: "view",
        label: "View Details",
        badgeLabel: "In Review",
        badgeTone: "yellow",
      };
    }
    // completed
    return {
      type: "view",
      label: "View Details",
      badgeLabel: status === "approved" ? "Approved" : "Rejected",
      badgeTone: status === "approved" ? "green" : "red",
    };
  }

  // CHECKER
  if (section === "pending_rectification") {
    return {
      type: "view",
      label: "View Details",
      badgeLabel: "Pending Rectification",
      badgeTone: "yellow",
    };
  }

  if (section === "pending_review") {
    const actionable = isObservationCheckerActionable(item, userId, userType);
    const isRecertification = attemptNo > 1;
    return {
      type: actionable ? "action" : "view",
      label: actionable
        ? isRecertification
          ? "Review Recertified Observation"
          : "Review Observation"
        : "View Details",
      badgeLabel: actionable
        ? isRecertification
          ? "Review Recertification"
          : "Pending For Review"
        : "In Review",
      badgeTone: "orange",
    };
  }

  // closed
  return {
    type: "view",
    label: "View Details",
    badgeLabel: status === "approved" ? "Approved" : "Rejected",
    badgeTone: status === "approved" ? "green" : "red",
  };
}

/**
 * Returns consistent status badge presentation across views and modals.
 */
export function getObservationStatusBadge(rawStatus) {
  const status = normalizeStatus(rawStatus);

  switch (status) {
    case "pending_maker":
    case "pending_contractor_maker":
      return {
        label: "Pending for Rectification",
        badgeClass: "bg-yellow-100 text-yellow-800 border-yellow-200",
      };
    case "pending_checker":
    case "pending_contractor_checker":
      return {
        label: "Pending for Review",
        badgeClass: "bg-orange-100 text-orange-800 border-orange-200",
      };
    case "approved":
    case "completed":
    case "closed":
      return {
        label: "Approved",
        badgeClass: "bg-green-100 text-green-800 border-green-200",
      };
    case "rejected":
    case "closed_rejected":
      return {
        label: "Rejected",
        badgeClass: "bg-red-100 text-red-800 border-red-200",
      };
    default:
      return {
        label: rawStatus || "Pending",
        badgeClass: "bg-slate-100 text-slate-800 border-slate-200",
      };
  }
}

/**
 * Resolves the display organization name for internal observation items,
 * falling back to logged-in user profile/session organization or "Internal".
 */
export function getInternalOrganizationName(item) {
  const itemOrg =
    item?.org_name ||
    item?.organization_name ||
    item?.organization?.name ||
    item?.organization?.organization_name ||
    item?.org?.name ||
    item?.org?.organization_name;
  if (
    typeof itemOrg === "string" &&
    itemOrg.trim() &&
    itemOrg.trim() !== "—" &&
    itemOrg.trim() !== "-"
  ) {
    return itemOrg.trim();
  }

  try {
    const rawUserData = localStorage.getItem("USER_DATA");
    if (rawUserData && rawUserData !== "undefined" && rawUserData !== "null") {
      const userData = JSON.parse(rawUserData);
      const name =
        userData?.organization_name ||
        userData?.org_name ||
        userData?.organization?.name ||
        userData?.organization?.organization_name ||
        userData?.company_name ||
        userData?.account?.organization_name;
      if (typeof name === "string" && name.trim()) {
        return name.trim();
      }
    }
  } catch {}

  try {
    const storedName =
      localStorage.getItem("ORGANIZATION_NAME") ||
      localStorage.getItem("org_name") ||
      localStorage.getItem("active_org_name") ||
      localStorage.getItem("active_organization_name");
    if (typeof storedName === "string" && storedName.trim()) {
      return storedName.trim();
    }
  } catch {}

  return "Internal";
}
